// The Admin view: HR tools behind the same sign-in, reached from the header's Admin tab.
// Sections are sub-tabs; each has its own path so a link or reload lands on the same one.
// HR.Agent sees Requests and Analytics; HR.Manager adds Documents; HR.Admin adds Sources. The API
// enforces the same split.

import { Role, type RoleName } from "@ask-hr/core/roles";
import { linkTo } from "../router.js";
import { AnalyticsPage } from "./AnalyticsPage.js";
import { DocumentsPage } from "./DocumentsPage.js";
import { RequestsPage } from "./RequestsPage.js";
import { SourcesPage } from "./SourcesPage.js";

export const ADMIN_SECTIONS = [
  ["requests", "Requests", Role.Agent],
  ["analytics", "Analytics", Role.Agent],
  ["documents", "Documents", Role.Manager],
  ["sources", "Sources", Role.Admin],
] as const;
export type AdminSection = (typeof ADMIN_SECTIONS)[number][0];

// roles: as rolesFromClaims gives them, so HR.Admin already includes HR.Manager and HR.Agent.
export function sectionsFor(roles: readonly RoleName[]) {
  return ADMIN_SECTIONS.filter(([, , need]) => roles.includes(need));
}

export function adminSection(path: string, roles: readonly RoleName[]): AdminSection {
  const s = path.split("/")[2];
  const allowed = sectionsFor(roles);
  return allowed.find(([id]) => id === s)?.[0] ?? allowed[0]?.[0] ?? "requests";
}

export function Admin({ path, roles }: { path: string; roles: readonly RoleName[] }) {
  const section = adminSection(path, roles);
  const openId = section === "requests" ? (/^\/admin\/requests\/([0-9a-z]+)$/.exec(path)?.[1] ?? null) : null;
  return (
    <>
      <nav className="subtabs" aria-label="Admin sections">
        {sectionsFor(roles).map(([id, label]) => (
          <a key={id} href={`/admin/${id}`} onClick={linkTo(`/admin/${id}`)} aria-current={section === id ? "page" : undefined}>
            {label}
          </a>
        ))}
      </nav>
      {section === "requests" ? <RequestsPage openId={openId} /> : null}
      {section === "analytics" ? <AnalyticsPage /> : null}
      {section === "documents" ? <DocumentsPage canManageSources={roles.includes(Role.Admin)} /> : null}
      {section === "sources" ? <SourcesPage /> : null}
    </>
  );
}
